<template>
  <div
    :class="[hideHardshipLine ? 'max-w-[1430px]' : 'max-w-[750px]', 'mb-16']"
  >
    <form @submit.prevent="handleSubmit">
      <div class="space-y-4">
        <!-- First Name -->
        <div class="form-group">
          <input
            v-model="form.firstName"
            type="text"
            class="w-full border rounded px-3 py-2"
            :class="{ 'border-red-500': v$.firstName.$error }"
            placeholder="First Name*"
          />
          <span v-if="v$.firstName.$error" class="text-red-500 text-sm"
            >First name is required</span
          >
        </div>

        <!-- Last Name -->
        <div class="form-group">
          <input
            v-model="form.lastName"
            type="text"
            class="w-full border rounded px-3 py-2"
            :class="{ 'border-red-500': v$.lastName.$error }"
            placeholder="Last Name*"
          />
          <span v-if="v$.lastName.$error" class="text-red-500 text-sm"
            >Last name is required</span
          >
        </div>

        <!-- Contact Number -->
        <div class="form-group">
          <input
            v-model="form.phoneNumber"
            type="tel"
            class="w-full border rounded px-3 py-2"
            :class="{ 'border-red-500': v$.phoneNumber.$error }"
            placeholder="Contact Number*"
          />
          <span v-if="v$.phoneNumber.$error" class="text-red-500 text-sm"
            >Valid contact number is required</span
          >
        </div>

        <!-- Email -->
        <div class="form-group">
          <input
            v-model="form.emailAddress"
            type="email"
            class="w-full border rounded px-3 py-2"
            :class="{ 'border-red-500': v$.emailAddress.$error }"
            placeholder="Email*"
          />
          <span v-if="v$.emailAddress.$error" class="text-red-500 text-sm"
            >Valid email is required</span
          >
        </div>

        <!-- Customer Radio -->
        <div>
          <h3 class="mb-2 font-bold">I am a OnePass mobile Customer*</h3>
          <div class="flex flex-col">
            <label class="inline-flex items-center">
              <input
                v-model="form.ccCustomer"
                type="radio"
                :value="true"
                class="form-radio"
              />
              <span class="ml-2 text-black">Yes</span>
            </label>
            <label class="inline-flex items-center">
              <input
                v-model="form.ccCustomer"
                type="radio"
                :value="false"
                class="form-radio"
              />
              <span class="ml-2 text-black">No</span>
            </label>
          </div>
          <span v-if="v$.ccCustomer.$error" class="text-red-500 text-sm"
            >Please select an option</span
          >
        </div>

        <!-- Complaint Area -->
        <div class="form-group">
          <select
            v-model="form.complaintArea"
            class="w-full border rounded px-3 py-2"
            :class="{ 'border-red-500': v$.complaintArea.$error }"
          >
            <option value="" disabled>
              Which account are you experiencing issues with?
            </option>
            <option value="MOBILE">Prepaid Mobile</option>
            <option value="DATA_ONLY">
              Prepaid Mobile broadband (data only)
            </option>
          </select>
          <span v-if="v$.complaintArea.$error" class="text-red-500 text-sm"
            >Please select an issue type</span
          >
        </div>

        <!-- Description -->
        <div class="form-group">
          <label class="mb-2"
            >Description of issue * [Please provide as much information as
            possible to assist. Do not include any sensitive information eg bank
            details] 2000 character limit</label
          >
          <textarea
            v-model="form.description"
            class="w-full border rounded px-3 py-2 h-20"
            :class="{ 'border-red-500': v$.description.$error }"
          ></textarea>
          <span v-if="v$.description.$error" class="text-red-500 text-sm">
            <span v-if="v$.description.required.$invalid"
              >Description is required.</span
            >
            <span v-else-if="v$.description.maxLength.$invalid"
              >Maximum 2000 characters allowed.</span
            >
          </span>
        </div>

        <!-- Privacy Agreement -->
        <div class="form-group">
          <label class="inline-flex items-center">
            <input
              v-model="form['agree-terms']"
              type="checkbox"
              class="form-checkbox text-red-600"
            />
            <span class="ml-2 text-black">
              I agree with the terms of the
              <a href="/privacy" class="text-[#9900f1] hover:underline"
                >OnePass mobile Privacy Policy</a
              >
            </span>
          </label>
          <span
            v-if="v$['agree-terms'].$error"
            class="text-red-500 text-sm block"
          >
            {{ v$["agree-terms"].$errors[0]?.$message }}
          </span>
        </div>
      </div>

      <p class="text-font-primary my-4">
        We aim to get back to you within 2 business days.
      </p>

      <p v-if="!hideHardshipLine" class="text-font-primary mb-6">
        If you are dealing with an urgent issue such as
        <a
          href="/legals-financial-hardship"
          class="text-red-600 hover:underline"
          >financial hardship</a
        >, disconnection of your service, or if a disconnection is about to
        occur, please call us on 1300 222 825.
      </p>

      <button
        type="submit"
        class="bg-[#9900f1] text-white px-6 py-2 rounded hover:bg-[#9900f1]transition-colors"
        :disabled="isSubmitting"
      >
        {{ isSubmitting ? "Submitting..." : "SUBMIT" }}
      </button>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { required, email, maxLength, helpers } from "@vuelidate/validators";
import { useToast } from "vue-toastification";
import { CheckIcon, XMarkIcon } from "@heroicons/vue/24/solid";

const { registerComplaint } = useBackendService();

defineProps<{
  hideHardshipLine?: boolean;
}>();

const emit = defineEmits(["submitted"]);

const toast = useToast();
const isSubmitting = ref(false);

const form = reactive({
  firstName: "",
  lastName: "",
  phoneNumber: "",
  emailAddress: "",
  ccCustomer: null as boolean | null,
  complaintArea: "",
  description: "",
  "agree-terms": false,
});

const rules = {
  firstName: { required },
  lastName: { required },
  phoneNumber: {
    required,
    validPhone: helpers.regex(/^(?:\+?61|0)[2-478](?:[ -]?[0-9]){8}$/),
  },
  emailAddress: { required, email },
  ccCustomer: { required },
  complaintArea: { required },
  description: { required, maxLength: maxLength(2000) },
  "agree-terms": {
    required: helpers.withMessage(
      "You must agree to the privacy policy",
      required
    ),
    isTrue: helpers.withMessage(
      "You must agree to the privacy policy",
      (val: boolean) => val === true
    ),
  },
};

const v$ = useVuelidate(rules, form);

const handleSubmit = async () => {
  const result = await v$.value.$validate();
  if (!result) return;

  isSubmitting.value = true;
  try {
    const payload = {
      ...form,
      ccCustomer: form.ccCustomer === true ? "YES" : "NO",
      "agree-terms": form["agree-terms"] ? "YES" : "NO",
    };

    await registerComplaint(payload);

    toast.success("Your request has been submitted.", {
      timeout: 3000,
      closeButton: XMarkIcon,
      icon: CheckIcon,
    });

    // reset form
    form.firstName = "";
    form.lastName = "";
    form.phoneNumber = "";
    form.emailAddress = "";
    form.ccCustomer = null;
    form.complaintArea = "";
    form.description = "";
    form["agree-terms"] = false;
    v$.value.$reset();

    emit("submitted");
  } catch (err: any) {
    const backendMessage = err?.data?.error?.message || "";
    toast.error(
      backendMessage || "Failed to submit the form. Please try again."
    );
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
.mandatory-text {
  color: var(--text-primary);
}
h2 {
  font-size: 30px;
  font-weight: 700;
}
h3 {
  font-size: 24px;
  font-weight: 500;
}
h4 {
  font-size: 18px;
  font-weight: 500;
}
h5 {
  font-size: 16px;
  font-weight: 400;
}
.form-radio {
  @apply h-4 w-4 border-gray-300 focus:ring-red-500;
}
.form-checkbox {
  @apply h-4 w-4 border-gray-300 rounded focus:ring-red-500;
}
</style>
